import {useContext} from 'react';
import {createRoot} from 'react-dom/client';
import {useQuery} from '@tanstack/react-query';
import {Base, PanelModelBase, Question, setLicenseKey} from 'survey-core';
import {Model} from 'survey-react-ui';
import {colors, sizes} from 'patient-ping-remedy/packages/theme';
import {ClientProfile} from '@/api/dto/client-profile';
import {CarecoApiContext} from '@/app-context/careco-api-context';
import {useActiveRoster} from '@/store/roster_store';
import {phoneTypeOptions} from '@manual_encounters/constants/ExtendedDemographicsOptions';
import DisplayHelpers from '@/helpers/display_helpers';
import Helpers from '@/helpers/helpers';
import InfoIconTooltip from '@common/InfoIconTooltip';

export function renderInfoIcon(base: Base, htmlElement: HTMLElement) {
  // @ts-expect-error - custom property
  const tooltipContent: string = base.tooltip;

  if (tooltipContent && htmlElement) {
    const titleElement = htmlElement.querySelector<HTMLElement>(".sv-string-viewer");

    if (titleElement) {
      const container = document.createElement('span');
      container.style.marginLeft = sizes.xSmall;
      container.classList.add('info-tooltip');

      removeInfoIcon(titleElement);
      titleElement.appendChild(container);

      const root = createRoot(container);
      root.render(<InfoIconTooltip color={colors.charcoal} html={generateHtmlFromText(tooltipContent)} text={tooltipContent}/>);
    }
  }
}

function generateHtmlFromText(content: string) {
  return content
    .split('\n')
    .map((line, index) => <span key={index}>{line.trim()}</span>);
}

export function removeInfoIcon(htmlElement: HTMLElement) {
  htmlElement.querySelector('.info-tooltip')?.remove();
}

export function useSurveyJSForm(useLicense?: boolean) {
  const {carecoApi} = useContext(CarecoApiContext);
  const currentRosterNpi = useActiveRoster()?.npi;

  useQuery({
    queryKey: ['surveyJSLicenseKey'],
    queryFn: () => carecoApi?.getSurveyJSLicenseKey(),
    select(data) {
      if (data) {
        setLicenseKey(data.licenseKey);
      }
    },
    enabled: useLicense && !!carecoApi,
  });


  const initForm = (model: Model, patient?: ClientProfile, addNpi?: boolean) => {
    model.questionErrorLocation = 'bottom';
    model.questionTitleTemplate = '{require} {title}';
    model.showCompleteButton = false;

    if (patient) {
      const address = patient.patient.address;
      const gender = DisplayHelpers.genderFromEnum(patient.patient.gender);
      model.mergeData({
        'client|first_name': patient.patient.firstName,
        'client|last_name': patient.patient.lastName,
        'client|birthdate': patient.patient.dateOfBirth,
        'client|gender': gender === 'Non-binary'
          ? 'Gender Non-Conforming' : gender,
        'client|street': address?.address1 ?? '',
        'client|address2': address?.address2 ?? '',
        'client|city': address?.city,
        'client|state': address?.state,
        'client|zip': address?.zip,
        'client|phone_type': phoneTypeOptions.find((phoneType) =>
          phoneType.value === patient.patientPhoneNumbers.filter(phone => phone.type)[0]?.type)?.label ?? '',
        'client|phone': DisplayHelpers.formatPhoneNumber(
          patient.patientPhoneNumbers.filter(phone => phone.phone)[0]?.phone
        ),
      });
    }

    if (addNpi && currentRosterNpi) {
      model.mergeData({
        'facility|npi': currentRosterNpi,
      });
    }
  };

  const enableRadioClear = (question: Question, htmlElement: HTMLElement) => {
    if (!question?.getType || question.getType() !== 'radiogroup' || !htmlElement) return;

    const radioInputs = htmlElement.querySelectorAll<HTMLInputElement>('input[type="radio"]');
    radioInputs.forEach((radio) => {
      if (radio.dataset.clearHandlerAttached === '1') return;
      radio.dataset.clearHandlerAttached = '1';

      radio.addEventListener('click', () => {
        if (question.value !== undefined && String(question.value) === radio.value) {
          question.clearValue();
        }
      });
    });
  };

  const initInfoIcons = (model: Model) => {
    model.getAllPanels().filter((panel): panel is PanelModelBase => 'getWrapperElement' in panel).forEach((panel) => {
      renderInfoIcon(panel, panel.getWrapperElement());
    });
    model.onAfterRenderPanel.add((_, options) => {
      renderInfoIcon(options.panel, options.htmlElement);
    });

    model.getAllQuestions().forEach((question) => {
      renderInfoIcon(question, question.getWrapperElement());
      preventNumberInputScroll(question, question.getWrapperElement());
      enableRadioClear(question, question.getWrapperElement());
    });
    model.onAfterRenderQuestion.add((_, options) => {
      renderInfoIcon(options.question, options.htmlElement);
      preventNumberInputScroll(options.question, options.htmlElement);
      enableRadioClear(options.question, options.htmlElement);
    });
  };

  const preventNumberInputScroll = (question: Question, htmlElement: HTMLElement) => {
    if (question?.inputType === 'number' && htmlElement) {
      const numericInput = htmlElement.getElementsByTagName('input')[0] as HTMLInputElement | undefined;
      if (!numericInput || numericInput.dataset.numericInputGuardAttached === '1') return;

      numericInput.dataset.numericInputGuardAttached = '1';

      numericInput.addEventListener('wheel', (event: WheelEvent) => {
        event.preventDefault();
        numericInput.blur();
      }, {passive: false});

      numericInput.addEventListener('keydown', (event: KeyboardEvent) => {
        if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
          event.preventDefault();
          numericInput.blur();
        }
      });
    }
  };

  const initValidation = (model: Model) => {
    model.onValidateQuestion.add((_, options) => {
      if (options.question.isRequired && (!Helpers.isDefined(options.value) || options.value.length === 0)) {
        options.error = 'This field is required.';
      }
    });
  };

  const scrollToError = () => {
    setTimeout(() => {
      const errorElement = document.querySelector(".sd-error");
      const inputElement = errorElement?.parentElement?.querySelector("input, select, textarea") as HTMLInputElement;
      errorElement?.scrollIntoView({ behavior: "smooth",  block: "center"});
      inputElement?.focus();
    }, 100);
  };

  return {
    initForm,
    initInfoIcons,
    initValidation,
    scrollToError,
    currentRosterNpi,
  };
}
